import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Plus, Search, User, Mail, Phone, MapPin, Award, Gem, Wallet, Backpack, Mountain, Heart, Users, Globe, Archive, ArchiveRestore } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TravellersList = () => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [travellers, setTravellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  // Data-integrity cleanup (2026-10-06) — see QuotesList.js's matching toggle.
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    fetchTravellers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryFilter, searchTerm, showDeleted]);

  const fetchTravellers = async () => {
    try {
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') params.append('category', categoryFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (showDeleted) params.append('include_deleted', 'true');
      
      const response = await axios.get(`${BACKEND_URL}/api/clients?${params.toString()}`);
      setTravellers(showDeleted ? response.data.filter((c) => c.deletedAt) : response.data);
    } catch (error) {
      toast.error('Failed to fetch travellers');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (ucc, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Archive this traveller? They disappear from the list but every record stays intact — you can restore them any time.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/clients/${ucc}`);
      toast.success('Traveller archived');
      fetchTravellers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to archive traveller');
    }
  };

  const handleRestore = async (ucc, e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await axios.post(`${BACKEND_URL}/api/clients/${ucc}/restore`);
      toast.success('Traveller restored');
      fetchTravellers();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to restore traveller');
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      luxury: 'bg-purple-50 text-purple-700 border-purple-200',
      budget: 'bg-blue-50 text-blue-700 border-blue-200',
      backpacking: 'bg-green-50 text-green-700 border-green-200',
      adventure: 'bg-orange-50 text-orange-700 border-orange-200',
      honeymoon: 'bg-pink-50 text-pink-700 border-pink-200',
      family: 'bg-indigo-50 text-indigo-700 border-indigo-200'
    };
    return colors[category] || colors.budget;
  };

  const CATEGORY_ICONS = { luxury: Gem, budget: Wallet, backpacking: Backpack, adventure: Mountain, honeymoon: Heart, family: Users };
  const CategoryIcon = ({ category, className = 'h-3.5 w-3.5' }) => {
    const I = CATEGORY_ICONS[category] || Globe;
    return <I className={className} strokeWidth={1.75} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary-700 mx-auto mb-4"></div>
          <p className="text-slate-500 font-medium">Loading travellers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/5 to-accent-600/5 rounded-3xl blur-3xl"></div>
          <div className="relative">
            <h1 className="text-5xl lg:text-6xl font-heading font-bold text-slate-900 tracking-tight mb-3">
              Travellers
            </h1>
            <p className="text-lg text-slate-600 font-light flex items-center gap-2">
              <User className="h-5 w-5" />
              Manage your guests & their travel profiles
            </p>
          </div>
        </div>
        
        <Link to="/clients/new">
          <Button 
            data-testid="add-traveller-btn"
            className="bg-gradient-to-br from-primary-700 to-primary-600 text-white hover:from-primary-800 hover:to-primary-700 shadow-xl shadow-primary-700/40 font-semibold px-10 py-6 rounded-2xl text-base transition-all duration-300 hover:scale-105"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Traveller
          </Button>
        </Link>
      </div>

      {isAdmin && (
        <div className="flex items-center justify-end">
          <button
            type="button"
            data-testid="toggle-archived-travellers-btn"
            onClick={() => setShowDeleted((v) => !v)}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-colors ${
              showDeleted ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            <Archive className="h-3.5 w-3.5" />
            {showDeleted ? 'Showing archived' : 'Show archived'}
          </button>
        </div>
      )}

      {/* Search & Filter Bar */}
      <div className="bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl p-8 shadow-xl">
        <div className="flex flex-col md:flex-row gap-6">
          <div className="flex-1 relative">
            <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 h-5 w-5 text-slate-400" />
            <Input
              data-testid="search-travellers-input"
              placeholder="Search by name, email, UCC, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-14 h-14 rounded-2xl border-slate-200 text-base focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
            />
          </div>
          
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger data-testid="category-filter" className="w-full md:w-64 h-14 rounded-2xl border-slate-200 text-base">
              <Award className="h-5 w-5 mr-2 text-slate-500" />
              <SelectValue placeholder="Travel Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              <SelectItem value="luxury">✨ Luxury Travel</SelectItem>
              <SelectItem value="budget">💰 Budget Travel</SelectItem>
              <SelectItem value="backpacking">🎒 Backpacking</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Travellers Grid */}
      {travellers.length === 0 ? (
        <div className="bg-gradient-to-br from-white/50 to-slate-50/50 backdrop-blur-sm border-2 border-dashed border-slate-200 rounded-3xl p-20 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-primary-100 to-primary-50 rounded-3xl flex items-center justify-center mx-auto mb-6">
            <User className="h-10 w-10 text-primary-700" />
          </div>
          <h3 className="text-2xl font-heading font-semibold text-slate-900 mb-3">No Travellers Found</h3>
          <p className="text-slate-500 mb-8 text-lg">Start by adding your first traveller to the system</p>
          <Link to="/clients/new">
            <Button className="bg-gradient-to-br from-primary-700 to-primary-600 text-white hover:from-primary-800 hover:to-primary-700 shadow-lg px-8 py-6 rounded-2xl text-base">
              <Plus className="h-5 w-5 mr-2" />
              Add First Traveller
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {travellers.map((traveller) => (
            <Link
              key={traveller.id}
              to={`/clients/${traveller.ucc}`}
              data-testid={`traveller-card-${traveller.ucc}`}
              className="group"
            >
              <div className="bg-white/80 backdrop-blur-xl border-2 border-slate-100 rounded-3xl p-8 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 cursor-pointer relative overflow-hidden">
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary-50/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-3xl"></div>
                
                <div className="relative z-10">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-primary-700 to-primary-600 rounded-2xl flex items-center justify-center shadow-lg shadow-primary-700/30">
                      <span className="text-white font-heading font-bold text-2xl">
                        {traveller.fullName.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <Badge className={`${getCategoryColor(traveller.travelCategory)} px-4 py-1.5 rounded-xl text-xs font-semibold border-2 inline-flex items-center gap-1.5`}>
                      <CategoryIcon category={traveller.travelCategory} /> {traveller.travelCategory}
                    </Badge>
                  </div>
                  
                  {/* Traveller Info */}
                  <h3 className="text-xl font-heading font-bold text-slate-900 mb-2 group-hover:text-primary-700 transition-colors">
                    {traveller.fullName}
                  </h3>
                  <p className="text-xs text-slate-500 font-mono mb-5 tracking-wide">{traveller.ucc}</p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center text-sm text-slate-600">
                      <Mail className="h-4 w-4 mr-3 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{traveller.email}</span>
                    </div>
                    <div className="flex items-center text-sm text-slate-600">
                      <Phone className="h-4 w-4 mr-3 text-slate-400 flex-shrink-0" />
                      <span>{traveller.phone}</span>
                    </div>
                    {traveller.destinationsCovered && traveller.destinationsCovered.length > 0 && (
                      <div className="flex items-center text-sm text-slate-600">
                        <MapPin className="h-4 w-4 mr-3 text-slate-400 flex-shrink-0" />
                        <span>{traveller.destinationsCovered.length} destinations visited</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Footer Stats */}
                  {traveller.totalBookingsValue > 0 && (
                    <div className="mt-6 pt-6 border-t border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-slate-500 font-medium">Total Bookings</span>
                        <span className="font-bold text-lg text-primary-700">₹{traveller.totalBookingsValue.toLocaleString('en-IN')}</span>
                      </div>
                    </div>
                  )}
                  {isAdmin && (
                    <div className="mt-4 flex justify-end">
                      {showDeleted ? (
                        <Button
                          data-testid={`restore-traveller-btn-${traveller.ucc}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleRestore(traveller.ucc, e)}
                          className="text-emerald-600 hover:text-emerald-800"
                          title="Restore"
                        >
                          <ArchiveRestore className="h-4 w-4 mr-1.5" /> Restore
                        </Button>
                      ) : (
                        <Button
                          data-testid={`archive-traveller-btn-${traveller.ucc}`}
                          variant="ghost"
                          size="sm"
                          onClick={(e) => handleArchive(traveller.ucc, e)}
                          className="text-amber-600 hover:text-amber-800"
                          title="Archive (recoverable)"
                        >
                          <Archive className="h-4 w-4 mr-1.5" /> Archive
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
      
      {/* Results Count */}
      {travellers.length > 0 && (
        <div className="text-center">
          <p className="text-sm text-slate-500 font-medium">
            Showing {travellers.length} traveller{travellers.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default TravellersList;